import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from '@mui/material';
import api from '@/lib/api';
import { toast } from '@/lib/toast';

const EMPTY = {
  name: '',
  duration_minutes: '',
  price: '',
  capacity: '1',
  description: '',
};

const toForm = (service) => ({
  name: service?.name ?? '',
  duration_minutes: service?.duration_minutes != null ? String(service.duration_minutes) : '',
  price: service?.price != null ? String(service.price) : '',
  capacity: service?.capacity != null ? String(service.capacity) : '1',
  description: service?.description ?? '',
});

const ServiceDialog = ({ open, mode = 'create', service = null, onClose, onSaved }) => {
  const [formData, setFormData] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(mode === 'edit' && service ? toForm(service) : EMPTY);
    }
  }, [open, mode, service]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        duration_minutes: parseInt(formData.duration_minutes, 10),
        price: parseFloat(formData.price),
        capacity: parseInt(formData.capacity, 10),
        description: formData.description,
      };
      if (mode === 'edit' && service) {
        await api.put(`/services/${service.service_id}`, payload);
        toast.success('Service updated');
      } else {
        await api.post('/services', payload);
        toast.success('Service added');
      }
      onSaved?.();
      onClose();
    } catch (error) {
      const action = mode === 'edit' ? 'update' : 'add';
      toast.error(error.response?.data?.detail || `Failed to ${action} service`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {mode === 'edit' ? 'Edit service' : 'Add new service'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Service name"
              required
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="PT Session, Intro Consult, etc."
              data-testid="service-name-input"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Duration (min)"
                type="number"
                required
                fullWidth
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
              />
              <TextField
                label="Price ($)"
                type="number"
                inputProps={{ step: '0.01' }}
                required
                fullWidth
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              />
            </Stack>
            <TextField
              label="Capacity"
              type="number"
              required
              fullWidth
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting} data-testid="service-submit-button">
            {mode === 'edit' ? 'Save changes' : 'Add service'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ServiceDialog;
